import { navigationNamespace } from "@/store/modules/navigation";
import { patientNamespace } from "@/store/modules/patient";
import { Patient } from "@/store/modules/patient/types";
import { userNamespace } from "@/store/modules/user";
import _ from "lodash";
import { Component, Vue } from "vue-property-decorator";
import { Action, Getter } from "vuex-class";

const dynamicProperties = [
  "MAPLow",
  "MAPHigh",
  "HRLow",
  "HRHigh",
  "SystolicLow",
  "SystolicHigh",
  "SpO2Low",
  "TVHigh",
  "TVLow"
];

@Component({})
export default class EditLimits extends Vue {
  @Action("updateSettings", patientNamespace)
  updateSettings!: Function;

  @Action("hidePopup", navigationNamespace)
  hidePopup!: Function;

  @Getter("demo", userNamespace)
  demo!: boolean;

  @Getter("current", patientNamespace)
  current!: Patient;

  @Getter("config", patientNamespace)
  config!: any;

  @Getter("vars", patientNamespace)
  vars!: any;

  @Getter("userID", userNamespace)
  userID!: string;

  @Getter("canEdit", userNamespace)
  canEdit!: boolean;

  @Getter("editLimitsConfig", patientNamespace)
  editLimitsConfig!: any;

  @Getter("orderSetConfig", patientNamespace)
  orderSetConfig!: any;

  get orderTypes(): string[] {
    let array: any[] = [];
    if (!this.orderSetConfig) {
      return array;
    }
    this.orderSetConfig.forEach((f: any) => {
      if (f.types != null && f.types.length > 0) {
        array.push(f.types[0]);
        if (f.types.length > 1) {
          array.push(f.types[1]);
        }
      }
    });
    return array;
  }

  saving = false;
  closing = false;

  get hasOrderSets() {
    const orders = this.orderTypes.filter((key: string) => {
      const data = _.get(this.current, `body.current.${key}`, {
        age: -1,
        value: ""
      });

      return data.age >= 0;
    });
    return orders.length > 0;
  }

  getValue(key: string) {
    const data = _.get(this.current, `body.current.${key}`, {
      age: -1,
      value: ""
    });
    if (data.age >= 0) {
      return data.value;
    } else {
      return "";
    }
  }

  get OrderAge() {
    const orders = this.orderTypes
      .map((key: string) => {
        const data = _.get(this.current, `body.current.${key}`, {
          age: -1,
          value: ""
        });

        return data;
      })
      .filter((data: any) => data.age >= 0)
      .sort((a, b) => a.age - b.age);

    return orders[0].age;
  }

  getAge(key: string) {
    const data = _.get(this.current, `body.current.${key}`, {
      age: -1,
      value: ""
    });
    if (data.age >= 0) {
      return data.age;
    } else {
      return -1;
    }
  }

  get noEdit() {
    return !this.canEdit;
  }

  SystolicLow = 0;
  SystolicLowRanges = [];

  SystolicHigh = 0;
  SystolicHighRanges = [];

  MAPHigh = 0;
  MAPHighRanges = [];

  MAPLow = 0;
  MAPLowRanges = [];

  HRLow = 0;
  HRLowRanges = [];
  HRHigh = 0;
  HRHighRanges = [];

  SpO2Low = 0;
  SpO2LowRanges = [];

  TVHigh = 0;
  TVHighRanges = [];

  TVLow = 0;
  TVLowRanges = [];

  ptLimits: Record<string, any> = {};

  setLimitValue(limit: string, e: any) {
    (<any>this)[limit] = e;
  }

  get settingsConfig() {
    return [
      {
        name: "BP Systolic",
        unit: "mmHg",
        types: [
          {
            items: this.SystolicLowRanges,
            model: this.SystolicLow,
            modelName: "SystolicLow",
            show: this.showSysBPSettings
          },
          {
            items: this.SystolicHighRanges,
            model: this.SystolicHigh,
            modelName: "SystolicHigh",
            show: this.showSysBPSettings
          }
        ],
        show: this.showSysBPSettings
      },
      {
        name: "BP MAP",
        unit: "mmHg",
        types: [
          {
            items: this.MAPLowRanges,
            model: this.MAPLow,
            modelName: "MAPLow",
            show: true
          },
          {
            items: this.MAPHighRanges,
            model: this.MAPHigh,
            modelName: "MAPHigh",
            show: true
          }
        ],
        show: true
      },
      {
        name: "Heart Rate",
        unit: "BPM",
        types: [
          {
            items: this.HRLowRanges,
            model: this.HRLow,
            modelName: "HRLow",
            show: true
          },
          {
            items: this.HRHighRanges,
            model: this.HRHigh,
            modelName: "HRHigh",
            show: true
          }
        ],
        show: true
      },
      {
        name: "SpO2",
        unit: "%",
        types: [
          {
            items: this.SpO2LowRanges,
            model: this.SpO2Low,
            modelName: "SpO2Low",
            show: true
          },
          {
            show: false
          }
        ],
        show: true
      },
      {
        name: "Tidal Volume / IBW",
        unit: "mL/kg",
        types: [
          {
            items: this.TVLowRanges,
            model: this.TVLow,
            modelName: "TVLow",
            show: this.showTvSettings
          },
          {
            items: this.TVHighRanges,
            model: this.TVHigh,
            modelName: "TVHigh",
            show: this.showTvSettings
          }
        ],
        show: this.showTvSettings
      }
    ];
  }

  mounted() {
    let limitConfig = this.editLimitsConfig;
    limitConfig.forEach((rule: any) => {
      if (rule && rule.delta != null && rule.delta > 0) {
        // new way
        if (!this.current.body.current[rule.variable]) {
          return;
        }
        const value = this.current.body.current[rule.variable].value;
        const text = this.current.body.current[rule.variable].text;
        const def = text == "0" ? value : Number(text);
        const icon = rule.def == def ? "•" : "*"; //←◄
        const items = [];
        const min =
          !this.showSysBPSettings && rule.variable == "MAPLow" ? 25 : rule.min;

        for (let i = rule.max; i >= min; i -= rule.delta) {
          if (value > i && i + def > value) {
            items.push({ text: value + " *", value: value });
          }
          const txt = def === i ? i + " •" : i; //←◄
          items.push({ text: txt, value: i });
        }
        this.ptLimits[rule.variable] = {
          items,
          value
        };

        // old way
        (<any>this)[rule.variable] = this.current.body.current[
          rule.variable
        ].value;
        (<any>this)[rule.variable + "Ranges"] = items;
      }
    });
  }

  get showSysBPSettings() {
    const ageCutOff = _.get(this.vars, "logicRules.RuleBP.sysAgeCutoff", 14);
    return this.current.census.age > ageCutOff;
  }

  get showTvSettings(): boolean {
    const showTv: boolean =
      _.get(this, "current.body.atoms.['ventStatus'].age", -1) !== -1;
    return showTv;
  }

  save(close: boolean = true) {
    this.saving = true;
    this.closing = close;
    const settings: Record<string, any> = {};
    dynamicProperties.forEach(key => {
      const propValue = (<any>this)[key];
      if (propValue) {
        settings[key] = propValue;
      }
    });

    const finish = () => {
      if (close) {
        this.hidePopup();
      }
      this.saving = false;
    };

    if (this.demo) {
      finish();
    } else {
      this.updateSettings({
        id: this.current.census.roomSys,
        settings
      }).then(finish);
    }
  }
}
